import { hexToHsl, hslToHex, hslToString, parseHslString } from '@/lib/theme/hsl-utils';
import { cn } from '@/lib/utils/cn';
import { Input } from './input';
import { Label } from './label';

interface ColorPickerProps {
  /** HSL string ("H S% L%"). Empty / null means "no override". */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Preset swatches shown above the picker for quick selection. */
  swatches?: string[];
  className?: string;
  /** Show the "Reset" button to clear the override. */
  resettable?: boolean;
}

export function ColorPicker({
  value,
  onChange,
  swatches,
  className,
  resettable = true,
}: ColorPickerProps) {
  const hsl = value ? parseHslString(value) : null;
  const hex = hsl ? hslToHex(hsl) : '#000000';

  const handleHexChange = (next: string) => {
    const parsed = hexToHsl(next);
    if (parsed) {
      onChange(hslToString(parsed));
    }
  };

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center gap-3">
        <div className="relative">
          <input
            type="color"
            value={hex}
            onChange={(e) => handleHexChange(e.target.value)}
            className="h-10 w-12 cursor-pointer rounded-md border border-input bg-background"
            aria-label="Primary color"
          />
        </div>
        <div className="flex-1 space-y-1">
          <Label htmlFor="color-hex" className="text-xs text-muted-foreground">
            Hex
          </Label>
          <Input
            id="color-hex"
            value={hex.toUpperCase()}
            onChange={(e) => handleHexChange(e.target.value)}
            className="font-mono text-xs"
          />
        </div>
      </div>

      {swatches && swatches.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {swatches.map((swatch) => (
            <button
              key={swatch}
              type="button"
              onClick={() => handleHexChange(swatch)}
              className={cn(
                'h-7 w-7 rounded-md border-2 transition-transform hover:scale-110',
                hex.toLowerCase() === swatch.toLowerCase()
                  ? 'border-ring shadow-sm'
                  : 'border-transparent',
              )}
              style={{ backgroundColor: swatch }}
              aria-label={`Select ${swatch}`}
            />
          ))}
        </div>
      )}

      {resettable && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          Reset to preset
        </button>
      )}
    </div>
  );
}
